"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { StackMapFrame } from "./StackMapFrame";

/**
 * /3d-navigator/<slug>: the project's 3D model filling the screen under the
 * site header, with links to the other 3D projects. The chosen home is kept
 * in the address (#b=…&s=…&f=…), so a shared link opens the same home.
 */
export function StackMapPage({
  slug,
  propertyId,
  propertyName,
  district,
  others,
}: {
  slug: string;
  propertyId: string;
  propertyName: string;
  district: string;
  others: Array<{ slug: string; name: string }>;
}) {
  // The hash is read after mount: the server has no address hash.
  const [hash, setHash] = useState<string | null>(null);
  const router = useRouter();
  useEffect(() => setHash(window.location.hash), []);

  // A mouse wheel moves the project row sideways, not the page. React wheel
  // handlers are passive and cannot stop the page scroll, so this one is native.
  const strip = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const row = strip.current;
    if (!row) return;
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const max = row.scrollWidth - row.clientWidth;
      if ((event.deltaY < 0 && row.scrollLeft <= 0) || (event.deltaY > 0 && row.scrollLeft >= max)) return;
      event.preventDefault();
      row.scrollLeft += event.deltaY;
    };
    row.addEventListener("wheel", onWheel, { passive: false });
    return () => row.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <div className="flex h-[calc(100dvh-80px)] flex-col">
      <div className="border-b border-line bg-white">
        <div className="mx-auto flex max-w-site items-center gap-3 px-4 py-2.5 md:gap-6 md:px-8 md:py-3">
          <div className="min-w-0 flex-1 md:flex-none">
            <h1 className="truncate font-display text-base font-semibold tracking-tight text-ink md:text-xl">{propertyName} in 3D</h1>
            {district && <p className="hidden text-xs text-ink-muted md:block">{district}</p>}
          </div>
          {/* Phones get one compact picker so the 3D keeps most of the screen. */}
          <select
            aria-label="Switch project"
            value={slug}
            onChange={(event) => router.push(event.target.value ? `/3d-navigator/${event.target.value}` : "/3d-navigator")}
            className="h-9 max-w-[45%] shrink-0 rounded-full border border-line bg-white px-3 text-sm font-medium text-ink md:hidden"
          >
            <option value={slug}>Other projects</option>
            {others.map((other) => (
              <option key={other.slug} value={other.slug}>
                {other.name}
              </option>
            ))}
            <option value="">All projects</option>
          </select>
          {/* One row that scrolls sideways, so more projects never push the 3D down. */}
          <nav aria-label="Other projects in 3D" className="hidden min-w-0 flex-1 items-center gap-2 text-sm md:flex">
            <span className="shrink-0 text-ink-muted">Also in 3D:</span>
            <div
              ref={strip}
              className="flex min-w-0 flex-1 gap-2 overflow-x-auto whitespace-nowrap pr-6 [mask-image:linear-gradient(to_right,black_calc(100%-32px),transparent)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {others.map((other) => (
                <Link
                  key={other.slug}
                  href={`/3d-navigator/${other.slug}`}
                  className="shrink-0 rounded-full border border-line px-3 py-1 font-medium text-ink hover:border-ink"
                >
                  {other.name}
                </Link>
              ))}
            </div>
            <Link href="/3d-navigator" className="shrink-0 px-1 font-medium text-ink-muted underline underline-offset-4 hover:text-ink">
              All
            </Link>
          </nav>
          <Link
            href={`/single-property/${slug}`}
            className="ml-auto hidden shrink-0 whitespace-nowrap text-sm font-semibold text-ink underline decoration-line underline-offset-4 md:inline"
          >
            Project details
          </Link>
        </div>
      </div>
      {/* z-31 keeps the floating chat button (z-30) from covering the home card */}
      <div className="relative z-[31] flex-1 bg-[#e9eef2]">
        {hash !== null && (
          <StackMapFrame
            slug={slug}
            propertyId={propertyId}
            propertyName={propertyName}
            embed="page"
            place="navigator"
            hash={hash}
            className="absolute inset-0 h-full w-full border-0"
            onHash={(next) => window.history.replaceState(null, "", next === "#" ? window.location.pathname : next)}
          />
        )}
      </div>
    </div>
  );
}
